import { DataTable } from "primereact/datatable";
import styles from "./MapTable.module.css";
import { motion } from "framer-motion";
import { Column } from "primereact/column";
import { Plottable, RiskCluster, SinglePlottable } from "./types";
import { formatExactMoneyDollars } from "../Money";
import { RefObject, useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "../../../themes/Theme";
import { Button } from "primereact/button";
import { formatMiles, haversineDistanceMiles } from "./util";

type MapTableProps = {
  selectedLocation: Plottable | undefined;
  selectedCluster: RiskCluster | undefined;
  allAddresses: Plottable[];
  isForScreenshot?: boolean;
  tableRef?: RefObject<HTMLDivElement>;
};

const formatMoney = (val: number) =>
  formatExactMoneyDollars(val, { withDollarSign: true, withRounding: true });

export const MapTable: React.FC<MapTableProps> = ({
  selectedLocation,
  selectedCluster,
  allAddresses,
  isForScreenshot = false,
  tableRef,
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const radiusMiles = selectedCluster?.radius;
  const isShowingCluster = radiusMiles !== undefined;

  const nearbyLocations: Plottable[] = useMemo(() => {
    if (selectedLocation === undefined) {
      return [];
    }
    const ungroupedAddresses = allAddresses.flatMap((plottable) => {
      if (plottable.type === "grouped") {
        return plottable.group;
      }
      return [plottable];
    });
    const addressesInPolicy = ungroupedAddresses.filter(
      (address) => address.isFromCurrentReport
    );
    const addressesOutOfPolicy = ungroupedAddresses.filter(
      (address) => !address.isFromCurrentReport
    );

    const getClosestAddresses = (
      addresses: Plottable[],
      numberToShow: number = 5
    ) => {
      const addressesWithDistance: Plottable[] = [];
      for (const address of addresses) {
        const dist = haversineDistanceMiles(selectedLocation, address);
        if (dist !== 0) {
          addressesWithDistance.push({
            ...address,
            distanceFromKeyPoint: dist,
          });
        }
      }
      return addressesWithDistance
        .sort(
          (a, b) =>
            (a.distanceFromKeyPoint ?? 0) - (b.distanceFromKeyPoint ?? 0)
        )
        .slice(0, numberToShow);
    };

    const closestAddressesInPolicy = getClosestAddresses(addressesInPolicy);
    const closestAddressesOutOfPolicy =
      getClosestAddresses(addressesOutOfPolicy);

    return closestAddressesInPolicy.concat(closestAddressesOutOfPolicy);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  }, [selectedLocation]);

  const selectedPlottables = useMemo(
    () =>
      selectedCluster && selectedCluster.included
        ? selectedCluster?.included
        : selectedLocation
          ? [selectedLocation].concat(nearbyLocations)
          : [],
    [selectedCluster, selectedLocation, nearbyLocations]
  );

  const singlePlottables = useMemo(
    () =>
      selectedPlottables
        .flatMap((plottable) => {
          if (plottable.type === "grouped") {
            return plottable.group;
          }
          return [plottable];
        })
        .map((singlePlottable) => {
          return {
            ...singlePlottable,
            groupKey: `${
              singlePlottable.distanceFromKeyPoint
                ? "nearby_location"
                : "selected"
            }_${singlePlottable.isFromCurrentReport}`,
          };
        })
        .sort((a, b) => b.groupKey.localeCompare(a.groupKey)),
    [selectedPlottables]
  );

  const [isShrunk, setIsShrunk] = useState(true);
  useEffect(() => {
    setIsShrunk(singlePlottables.length === 0);
  }, [singlePlottables]);

  const variants = {
    shrunk: {
      height: "100px",
    },
    expanded: {
      height: "48vh",
    },
  };

  return (
    <motion.div
      variants={isForScreenshot ? {} : variants}
      initial="shrunk"
      animate={isShrunk ? "shrunk" : "expanded"}
      transition={{ ease: "easeInOut", duration: 0.35 }}
      className={
        isForScreenshot ? styles.screenshotContainer : styles.container
      }
      ref={tableRef}
    >
      <DataTable
        scrollHeight={isForScreenshot ? undefined : "flex"}
        scrollable
        emptyMessage="No locations selected"
        value={singlePlottables}
        rowGroupMode="subheader"
        groupRowsBy="groupKey"
        rowClassName={(data: SinglePlottable) =>
          data.isFromCurrentReport
            ? isLightMode
              ? styles.blueTableRowLight
              : styles.blueTableRowDark
            : isLightMode
              ? styles.greenTableRowLight
              : styles.greenTableRowDark
        }
        rowGroupHeaderTemplate={(data: SinglePlottable, options) => {
          // This magic incantation removes the defualt <td> wrapper on the
          // group header, which lets us add our own <td>s with colspan set;
          // this aligns components of the subheader with the columns.
          options.customRendering = true;

          const relevantLocations = singlePlottables.filter(
            (plottable) => plottable.groupKey === data.groupKey
          );
          const totalTiv = relevantLocations.reduce((a, b) => a + b.tiv, 0);
          const textColor = data.isFromCurrentReport
            ? "var(--primary-color)"
            : "var(--green-500)";

          const tivTotalText = `${relevantLocations.length} location${
            relevantLocations.length === 1 ? "" : "s"
          }`;
          const totalTivString = formatMoney(totalTiv);
          const totalBppString = formatMoney(
            relevantLocations.reduce((a, b) => a + b.coverages.bpp, 0)
          );
          const totalBiString = formatMoney(
            relevantLocations.reduce((a, b) => a + b.coverages.bi, 0)
          );
          const totalBldgString = formatMoney(
            relevantLocations.reduce((a, b) => a + b.coverages.building, 0)
          );
          const totalOtherString = formatMoney(
            relevantLocations.reduce((a, b) => a + b.coverages.other, 0)
          );

          const headerText = isShowingCluster
            ? `${tivTotalText}${
                radiusMiles
                  ? `. Circle diameter is ${formatMiles(radiusMiles * 2)} mi`
                  : ""
              }`
            : data.groupKey?.startsWith("nearby_location")
              ? `Nearby ${
                  data.isFromCurrentReport ? "in policy" : "out of policy"
                }: ${tivTotalText}`
              : `Selected: ${tivTotalText}`;

          const styles = { fontWeight: "bold", color: textColor };
          return (
            <>
              <td colSpan={isShowingCluster ? 6 : 7} style={styles}>
                {headerText}
              </td>
              <td style={styles}>{totalTivString}</td>
              <td style={styles}>{totalBppString}</td>
              <td style={styles}>{totalBiString}</td>
              <td style={styles}>{totalBldgString}</td>
              <td style={styles}>{totalOtherString}</td>
              <td style={styles}></td>
            </>
          );
        }}
      >
        <Column header="Policy Number" field="policyNumber" />
        <Column header="Named Insured" field="namedInsured" />
        {!isShowingCluster && (
          <Column
            header="Distance From Selected"
            field="distanceFromKeyPoint"
            body={(data: SinglePlottable) =>
              data.distanceFromKeyPoint
                ? `${formatMiles(data.distanceFromKeyPoint)} mi`
                : undefined
            }
          />
        )}
        <Column header="Address" field="streetAddress" />
        <Column header="City" field="city" />
        <Column header="State" field="state" />
        <Column header="Zip" field="zip" />
        <Column
          header="TIV"
          field="tiv"
          body={(data: SinglePlottable) => formatMoney(data.tiv)}
        />
        <Column
          header="BPP"
          body={(data: SinglePlottable) => formatMoney(data.coverages.bpp)}
        />
        <Column
          header="BI"
          body={(data: SinglePlottable) => formatMoney(data.coverages.bi)}
        />
        <Column
          header="Bldg"
          body={(data: SinglePlottable) => formatMoney(data.coverages.building)}
        />
        <Column
          header="Other Cov"
          body={(data: SinglePlottable) => formatMoney(data.coverages.other)}
        />
        <Column
          header={
            <Button
              icon={isShrunk ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              onClick={() => setIsShrunk(!isShrunk)}
              severity="secondary"
              text
              style={{ padding: "4px", height: "32px", width: "32px" }}
              size="small"
            />
          }
        />
      </DataTable>
    </motion.div>
  );
};
